// src/services/qbankApi.ts
import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react';

import { env } from '@/env';
import { guardiaOspite } from '@/services/guardiaOspite';
import { getQbankAccessToken } from '@/lib/logto';
import type {
  LeagueInfo,
  LeagueTierInfo,
  MyLeaguesResponse,
  LeagueContextualRanking,
  LeagueCentre,
  LeagueRival,
  TierRankingResponse,
  Wallet,
} from '@/types/qbank';

/**
 * QBank API client (SPIKE, read-only). Talks to qbank at `/wallets/*`.
 *
 * Auth: a Bearer token from getQbankAccessToken(). In dev that's QBank's
 * internal test-OIDC token (Logto bypassed) — see src/lib/logto.ts.
 *
 * Response quirk: QBank returns `Ok(JsonSerializer.Serialize(...))` — a JSON
 * STRING sent as text/plain (no `{ data }` envelope). We read it as text and
 * JSON.parse (twice if double-encoded), tolerating plain-text error bodies.
 *
 * Dev: env.qbankApiUrl = '/qbank-api' (Vite proxy → local QBank :8080).
 * Prod: env.qbankApiUrl = 'https://qbank.qlash.gg'.
 */
async function parseQbankBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return null;
  try {
    let v: unknown = JSON.parse(text);
    if (typeof v === 'string') v = JSON.parse(v);
    return v;
  } catch {
    return text; // plain-text error body (e.g. "User not authorized")
  }
}

/**
 * Il tag della cache del SALDO. Esportato, e non scritto due volte come stringa, per la ragione
 * che questo repo ha gia' imparato a proprie spese con gli scope Logto: due letterali che devono
 * coincidere e vivono in due file sono una promessa che nessuno mantiene a memoria. Chi invalida
 * (`questApi`, sul claim riuscito) e chi fornisce (`getWallets`, qui sotto) devono nominare la
 * STESSA cosa, e un refuso da una parte sola non darebbe alcun errore: darebbe soltanto un saldo
 * che non si aggiorna — cioe' esattamente il difetto che questo tag esiste per chiudere, tornato
 * in forma silenziosa.
 */
export const QBANK_WALLET_TAG = 'Wallet' as const;

export const qbankApi = createApi({
  reducerPath: 'qbankApi',
  baseQuery: guardiaOspite('qbank', retry(
    fetchBaseQuery({
      baseUrl: env.qbankApiUrl,
      prepareHeaders: async (headers) => {
        const token = await getQbankAccessToken();
        if (token) headers.set('Authorization', `Bearer ${token}`);
        headers.set('x-platform-id', env.platformId);
        headers.set('Accept', 'application/json');
        return headers;
      },
    }),
    { maxRetries: 1 },
  )),
  keepUnusedDataFor: 120,
  refetchOnFocus: false,
  refetchOnReconnect: true,
  // ⚠️ AGGIUNTA PURAMENTE ADDITIVA (2026-08-30). Nessuna politica di cache di questo client e'
  // stata toccata — `keepUnusedDataFor`, `refetchOnFocus` e `refetchOnReconnect` sono quelli di
  // prima, di proposito: qbankApi e' superficie condivisa con altre sezioni e le sue politiche le
  // decide chi possiede il wallet, non chi passa di qui. Un `tagTypes` piu' un `providesTags` non
  // cambiano il comportamento di NESSUN consumatore esistente: finche' nessuno invalida quel tag,
  // la cache si comporta esattamente come prima. Servono solo a rendere il saldo INVALIDABILE
  // dall'esterno, che prima non era.
  tagTypes: [QBANK_WALLET_TAG],
  endpoints: (builder) => ({
    // GET /wallets/get (no currencyId) → ALL of the caller's wallets. `sub` is
    // taken from the JWT, never sent as a param.
    getWallets: builder.query<Wallet[], void>({
      query: () => ({
        url: '/wallets/get',
        responseHandler: parseQbankBody,
      }),
      transformResponse: (raw: unknown): Wallet[] => (Array.isArray(raw) ? (raw as Wallet[]) : []),
      /**
       * 🔴 QUESTA RIGA E' MANCATA AL PRIMO CLAIM VERO DELLA PIATTAFORMA (2026-08-30, 11:19:14Z).
       *
       * Il denaro si e' mosso alla prima e senza errori — istanza `claimed`, payout committed,
       * saldo essenze da 2100 a 2101 — e l'utente ha scritto *«fatto reclaim, confermato, ma non
       * vedo l'essenza fra le mie valute wallet»*. Un minuto dopo: *«ora si'»*. Il claim
       * invalidava `['Missions']` su `questApi` e nessuno diceva nulla a QUESTO client, che con
       * `keepUnusedDataFor: 120` e `refetchOnFocus: false` teneva il saldo vecchio fino a due
       * minuti.
       *
       * ⚠️ Il difetto non stava in nessuno dei due pezzi: due slice RTK, ognuno con una politica di
       * cache sensata, e nessuno responsabile della congiunzione. Il denaro attraversa il confine
       * fra i due; l'invalidazione, no.
       *
       * Senza `providesTags` l'invalidazione dall'altra parte e' un `dispatch` che non fa NULLA —
       * e non fallisce, non avverte, non lascia traccia. E' il modo peggiore in cui questa
       * correzione potrebbe essere annullata, perche' tutto continuerebbe a sembrare cablato.
       */
      providesTags: [QBANK_WALLET_TAG],
    }),

    // GET /leagues/my → the caller's ACTIVE league enrollments (tier, rank,
    // score, momentum). `sub` comes from the JWT. Needs `qbank:league:read`;
    // 403s (and self-hides) for users without the scope. Empty until a league
    // is ACTIVE.
    getMyLeagues: builder.query<MyLeaguesResponse, void>({
      query: () => ({
        url: '/leagues/my',
        responseHandler: parseQbankBody,
      }),
      transformResponse: (raw: unknown): MyLeaguesResponse => {
        const leagues = (raw as { leagues?: unknown } | null)?.leagues;
        return { leagues: Array.isArray(leagues) ? (leagues as MyLeaguesResponse['leagues']) : [] };
      },
    }),

    /**
     * GET /leagues/{leagueId}/ranking?sub=&radius= — the "±N around me" window.
     *
     * `sub` is what switches the endpoint into contextual mode; without it the
     * server answers with a paginated top-N instead, a different shape. Passing
     * someone else's sub needs `qbank:league:write`, so only ever pass our own.
     *
     * Errors worth knowing (verified in QBank): 409 LEAGUE_NOT_STARTED while the
     * league is CREATED, 409 LEADERBOARD_UNAVAILABLE while BOOTING/ENDING, 404
     * PLAYER_NOT_ENROLLED before the first credit. All of them mean "nothing to
     * show yet", not "broken" — the caller self-hides.
     */
    getContextualRanking: builder.query<
      LeagueContextualRanking,
      { leagueId: string; sub: string; radius?: number }
    >({
      query: ({ leagueId, sub, radius = 5 }) => ({
        url: `/leagues/${encodeURIComponent(leagueId)}/ranking?sub=${encodeURIComponent(sub)}&radius=${radius}`,
      }),
      transformResponse: (raw: unknown): LeagueContextualRanking => {
        const r = (raw ?? {}) as Record<string, unknown>;
        return {
          league: r.league as LeagueInfo,
          tier: null,
          center: (r.center ?? {}) as LeagueCentre,
          rivals: Array.isArray(r.rivals) ? (r.rivals as LeagueRival[]) : [],
        };
      },
    }),

    // GET /leagues/{leagueId}/tiers/{tierId}/ranking — standard (offset/limit)
    // mode: { league, tier, players[], myRank, myPage, pagination }. 409 while
    // the league is CREATED/BOOTING/ENDING. Players carry only `sub` (resolve
    // names via QSocials).
    getTierRanking: builder.query<
      TierRankingResponse,
      { leagueId: string; tierId: string; offset?: number; limit?: number }
    >({
      query: ({ leagueId, tierId, offset = 0, limit = 50 }) => ({
        url: `/leagues/${encodeURIComponent(leagueId)}/tiers/${encodeURIComponent(tierId)}/ranking?offset=${offset}&limit=${limit}`,
        responseHandler: parseQbankBody,
      }),
      transformResponse: (raw: unknown): TierRankingResponse => {
        const r = (raw ?? {}) as Partial<TierRankingResponse>;
        return {
          league: r.league as LeagueInfo,
          tier: r.tier as LeagueTierInfo,
          players: Array.isArray(r.players) ? r.players : [],
          myRank: r.myRank ?? null,
          myPage: r.myPage ?? null,
          pagination: r.pagination,
        };
      },
    }),
  }),
});

export const {
  useGetWalletsQuery,
  useGetMyLeaguesQuery,
  useGetContextualRankingQuery,
  useGetTierRankingQuery,
} = qbankApi;
